import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';

const TaxForms = () => {
  const [forms, setForms] = useState([
    {
      id: 1,
      formType: '1099-NEC',
      year: 2025,
      status: 'ready',
      recipients: 12,
      generatedDate: '2026-01-15',
      filedDate: null
    },
    {
      id: 2,
      formType: 'W-2',
      year: 2025,
      status: 'filed',
      recipients: 8,
      generatedDate: '2026-01-10',
      filedDate: '2026-01-20'
    },
    {
      id: 3,
      formType: 'Form 941',
      year: 2025,
      status: 'draft',
      recipients: 1,
      generatedDate: '2026-01-25',
      filedDate: null
    },
    {
      id: 4,
      formType: 'Sales Tax Return',
      year: 2026,
      status: 'pending',
      recipients: 1,
      generatedDate: null,
      filedDate: null
    }
  ]);

  const formTypeOptions = [
    { value: '1099-nec', label: '1099-NEC - Nonemployee Compensation' },
    { value: '1099-misc', label: '1099-MISC - Miscellaneous Income' },
    { value: 'w2', label: 'W-2 - Wage and Tax Statement' },
    { value: '941', label: 'Form 941 - Quarterly Federal Tax Return' },
    { value: 'sales-tax', label: 'Sales Tax Return' },
    { value: '1120', label: 'Form 1120 - Corporate Income Tax' }
  ];

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      ready: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      filed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors?.[status] || colors?.draft;
  };

  const getStatusIcon = (status) => {
    const icons = {
      draft: 'FileEdit',
      ready: 'CheckCircle',
      pending: 'Clock',
      filed: 'CheckCircle2',
      rejected: 'XCircle'
    };
    return icons?.[status] || 'FileText';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Tax Forms & Filing</h2>
          <p className="text-sm text-gray-600 mt-1">Generate, review, and file tax forms electronically</p>
        </div>
        <Button iconName="Plus">
          Generate New Form
        </Button>
      </div>
      {/* Quick Generate Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Generate</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select
            label="Form Type"
            placeholder="Select form type"
            options={formTypeOptions}
          />
          <Select
            label="Tax Year"
            placeholder="Select year"
            options={[
              { value: '2026', label: '2026' },
              { value: '2025', label: '2025' },
              { value: '2024', label: '2024' }
            ]}
          />
          <div className="flex items-end">
            <Button iconName="FileText" fullWidth>
              Generate Form
            </Button>
          </div>
        </div>
      </div>
      {/* Forms List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Form Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Year
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Recipients
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Generated
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {forms?.map((form) => (
              <tr key={form?.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <Icon name="FileText" size={18} className="text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">{form?.formType}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {form?.year}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex items-center gap-1 text-xs leading-5 font-semibold rounded-full ${getStatusColor(form?.status)}`}>
                    <Icon name={getStatusIcon(form?.status)} size={12} />
                    {form?.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {form?.recipients}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {form?.generatedDate || 'Not generated'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" iconName="Eye">
                      Preview
                    </Button>
                    <Button variant="ghost" size="sm" iconName="Download">
                      Download
                    </Button>
                    {form?.status === 'ready' && (
                      <Button variant="default" size="sm" iconName="Send">
                        File
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* E-Filing Info */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="CheckCircle" size={20} className="text-green-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-green-900 mb-1">Electronic Filing Enabled</h4>
            <p className="text-sm text-green-800">
              Your account is configured for electronic filing with the IRS and state tax authorities. 
              Forms marked as "ready" can be filed directly from this interface.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxForms;